import {AnyAction, WriteDocumentConfigError} from "@buildwithflux/core";
import {isDevEnv} from "@buildwithflux/shared";
import {defer, EMPTY, Subject, Subscription} from "rxjs";
import {catchError, debounceTime, filter, switchMap, takeUntil, tap} from "rxjs/operators";

import type {DocumentStore} from "modules/document/state/document";
import {useFeatureFlags} from "modules/feature_flags/hooks";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import type {DocumentStorage} from "modules/storage_engine/DocumentStorage";
import {createActionDiscriminator} from "modules/document/state/helpers/batchActions";
import {setDocumentConfigs} from "modules/document/state/actionCreators/documentActions";
import R from "resources/Namespace";

/**
 * Service that handles document config saves.
 *
 * This service receives actions via its onAction() method, called directly by the store's
 * service notification system after actions are processed.
 * It uses RxJS internally for action stream processing while fitting into the FluxServices DI pattern.
 *
 * The service saves document configs when setDocumentConfigs actions are dispatched,
 * with debouncing to avoid excessive writes.
 *
 * NOTE: Unlike the saveDocumentEpic, if this request fails we are not currently triggering
 * a network error (although we might want to in the future).
 *
 * The entire configs collection is resaved without diffing. We expect this to be fast
 * because the number of non-anonymous viewers of any document is small.
 */
export interface DocumentConfigService {
    /**
     * Called when an action is processed by the document store.
     * The service filters and processes relevant actions internally.
     */
    onAction: (action: AnyAction) => void;

    /**
     * Disposes of all subscriptions and cleans up resources.
     * Should be called when the service is no longer needed.
     */
    dispose: () => void;
}

export interface DocumentConfigServiceDependencies {
    documentStorage: DocumentStorage;
    documentStore: DocumentStore;
}

/**
 * Creates a DocumentConfigService instance.
 *
 * This factory function creates a service that handles document config saves
 * using RxJS for action stream processing with debouncing.
 *
 * @param dependencies - The service dependencies (documentStorage, documentStore)
 * @returns A DocumentConfigService instance
 */
export function createDocumentConfigService(dependencies: DocumentConfigServiceDependencies): DocumentConfigService {
    const {documentStorage, documentStore} = dependencies;
    const action$ = new Subject<AnyAction>();
    const destroy$ = new Subject<void>();
    const subscriptions: Subscription[] = [];
    let disposed = false;

    // Save document configs epic replacement
    // Handles saving document configs (view controls, zoom positions) on a per-user basis
    // NOTE: We use defer() to wrap the promise creation so that:
    // 1. Synchronous throws (e.g., when Firebase is terminated) become observable errors
    // 2. Errors are handled in-stream via catchError (not as unhandled promise rejections)
    // 3. The stream continues even if individual saves fail
    const saveConfigsSubscription = action$
        .pipe(
            // Use takeUntil(destroy$) FIRST to ensure the entire pipeline is cancelled
            // when dispose() is called. This is critical because debounceTime schedules
            // timers that could fire after unsubscribe() if not properly cancelled.
            takeUntil(destroy$),
            filter(createActionDiscriminator(setDocumentConfigs)),
            filter(() => !(useFeatureFlags.getState().disableAutoSave ?? false)),
            debounceTime(R.behaviors.storage.writeMaxWait),
            switchMap((_action) => {
                // Get document from Zustand store instead of Redux state
                const documentState = documentStore.getState().document;

                if (!documentState) return EMPTY;

                const saveStartInMs = Date.now();
                // Use defer() to ensure synchronous throws are caught as observable errors.
                // IMPORTANT: We attach a promise-level .catch() that logs and resolves (not rethrows)
                // to prevent unhandled promise rejections when the subscription is torn down
                // (either via dispose() during shutdown or via switchMap cancellation).
                // The RxJS catchError only handles errors while the subscription is active;
                // if the subscription is unsubscribed and the promise later rejects, it would
                // become an unhandled rejection at the process level without this promise-level catch.
                return defer(() =>
                    documentStorage.saveConfigs(documentState).catch((error) => {
                        // Log at promise level to ensure rejection is always handled,
                        // even if the RxJS subscription has been torn down
                        FluxLogger.captureError(
                            new WriteDocumentConfigError(
                                `Failed to write document configs (promise-level): ${JSON.stringify(error)}`,
                            ),
                        );
                        // Return undefined to resolve the promise (not rethrow)
                        // This prevents unhandled rejections during teardown
                        return undefined;
                    }),
                ).pipe(
                    tap(() => {
                        if (isDevEnv()) {
                            // eslint-disable-next-line no-console
                            console.info(`FLUX-PERF: save configs took ${Date.now() - saveStartInMs}ms`);
                        }
                    }),
                    // Keep catchError for defense-in-depth, though promise-level catch handles most cases
                    catchError((error) => {
                        FluxLogger.captureError(
                            new WriteDocumentConfigError(`Failed to write document configs: ${JSON.stringify(error)}`),
                        );
                        return EMPTY;
                    }),
                );
            }),
            // Top-level catchError to prevent any unexpected errors from crashing the process
            catchError((error) => {
                FluxLogger.captureError(
                    new WriteDocumentConfigError(
                        `Unexpected error in document config save stream: ${JSON.stringify(error)}`,
                    ),
                );
                return EMPTY;
            }),
        )
        .subscribe({
            // Explicit error handler to prevent unhandled promise rejections
            error: (error) => {
                FluxLogger.captureError(
                    new WriteDocumentConfigError(`Document config save subscription error: ${JSON.stringify(error)}`),
                );
            },
        });
    subscriptions.push(saveConfigsSubscription);

    const service: DocumentConfigService = {
        onAction: (action: AnyAction) => {
            if (disposed) {
                return;
            }
            action$.next(action);
        },
        dispose: () => {
            // Mark as disposed first to prevent any new actions from being processed
            disposed = true;

            // Emit on destroy$ to cancel the RxJS pipeline (including any pending debounce timers)
            // This is more reliable than just calling unsubscribe() because takeUntil
            // will complete the stream synchronously, cancelling any scheduled timers
            destroy$.next();
            destroy$.complete();

            // Also unsubscribe for completeness
            for (const subscription of subscriptions) {
                subscription.unsubscribe();
            }
            action$.complete();
        },
    };

    return service;
}
